import { SectionLabel } from '@/features/landing/components/SectionLabel'

/**
 * Founder + honest-alpha band. Pratfall trust: a competent product that
 * openly names its minor rough edges reads as more trustworthy. "Rough"
 * items are minor and forward-looking ONLY — never reliability or data
 * safety. No personal contact (per founder decision); the community link
 * lives in the FoundingSquad band.
 */
const SOLID = [
  'Rosters, roles and member verification',
  'Sessions, meetups, stream nights and full tournaments',
  'Discord channel binding and Twitch live detection',
  'Member dues and paid tournament pots through Stripe',
]

const ROUGH = [
  'iOS is on the way — Android and web are live today',
  'We’re adding more stats and reporting over the coming weeks',
  'A few screens are still getting their polish pass',
]

export function FounderNote() {
  return (
    <section className="relative border-b border-border/60 px-6 py-24 md:py-32">
      <div className="mx-auto max-w-4xl">
        <SectionLabel index="08" label="Straight from the founder" />

        <div className="mt-6 max-w-3xl space-y-5 text-lg leading-relaxed text-fg-muted text-pretty md:text-xl">
          <p>
            I&rsquo;ve been gaming my whole life. It started on Star Wars
            Galaxies, where a group of fellow nerds quested and raided
            together, yelling &ldquo;Leeroy Jenkins&rdquo; right before we got
            wiped. After I came home from Afghanistan, I struggled to reconnect
            with people but found solace in my gaming friends. The squad
            I&rsquo;d met whilst playing PlanetSide is what helped me normalise
            and cope. Ten years on, they&rsquo;re some of my closest friends
            &mdash; we&rsquo;ve met each other&rsquo;s families and had
            moments I&rsquo;ll never forget.
          </p>
          <p className="font-display text-xl font-semibold text-fg md:text-2xl">
            Gaming gave me those people.{' '}
            <span className="gradient-text">
              I&rsquo;m building e404 so you can find your lifelong gaming squad.
            </span>
          </p>
        </div>
        <p className="mt-5 font-display text-sm tracking-wide text-fg-muted">
          &mdash; James Palfrey, founder of e404
        </p>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          <HonestColumn heading="What's solid today" tone="solid" items={SOLID} />
          <HonestColumn heading="What's still rough" tone="rough" items={ROUGH} />
        </div>
      </div>
    </section>
  )
}

function HonestColumn({
  heading,
  tone,
  items,
}: {
  heading: string
  tone: 'solid' | 'rough'
  items: string[]
}) {
  return (
    <div className="rounded-xl border border-border bg-surface/70 p-6">
      <p className="font-display text-xs tracking-[0.2em] text-fg-subtle uppercase">
        <span className={tone === 'solid' ? 'text-success' : 'text-warning'}>
          //
        </span>{' '}
        {heading}
      </p>
      <ul className="mt-4 flex flex-col gap-2.5 text-sm text-fg-muted">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span
              aria-hidden
              className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                tone === 'solid' ? 'bg-success' : 'bg-warning'
              }`}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
